import React, { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { nanoid } from "nanoid";
import { apiPost, apiGet, isOnline } from "@/lib/api";
import { getCatalog } from "@/lib/catalog-cache";
import { enqueue, SYNC_PRIORITY } from "@/lib/sync-queue";
import { useOfflineFlha } from "@/hooks/use-offline-flha";
import { useSession } from "@/lib/auth-client";
import {
  useFlhaWizardStore,
  useFlhaWizardHydrated,
} from "@/stores/flha-wizard-store";
import {
  WizardShellV2,
  JobSetupV2,
  TasksV2,
  HighRiskV2,
  HazardsControlsPpeV2,
} from "@/components/flha/v2";
import { ReviewScreen } from "@/components/flha/review/review-screen";
import { SubmitSuccess } from "@/components/flha/submit-success";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { X } from "lucide-react";

export const Route = createFileRoute("/_wizard/flha/new-v2")({
  component: FlhaWizardV2,
});

// -- Task catalog type -------------------------------------------------------

interface TaskCatalogItem {
  id: string;
  isHighRisk: boolean;
  highRiskType: string | null;
}

// -- Dynamic steps (mirror V1) ----------------------------------------------

function useSteps() {
  const hasHighRisk = useFlhaWizardStore((s) => s.hasHighRiskTasks);
  return useMemo(() => {
    const steps: React.ComponentType[] = [JobSetupV2, TasksV2];
    if (hasHighRisk) steps.push(HighRiskV2);
    steps.push(HazardsControlsPpeV2);
    return steps;
  }, [hasHighRisk]);
}

function WizardSkeleton() {
  return (
    <div className="max-w-lg mx-auto space-y-4 pt-6">
      <div className="px-4 space-y-3">
        <Skeleton className="h-2 w-full rounded-full" />
        <Skeleton className="h-6 w-40" />
      </div>
      <div className="space-y-3 px-4 pt-4">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-16 w-full rounded-xl" />
        <Skeleton className="h-16 w-full rounded-xl" />
      </div>
    </div>
  );
}

// Step validation mirrors V1 exactly — same store fields, same rules.
function useStepValidation(step: number): boolean {
  const projectId = useFlhaWizardStore((s) => s.projectId);
  const selectedTaskIds = useFlhaWizardStore((s) => s.selectedTaskIds);
  const hazards = useFlhaWizardStore((s) => s.hazards);
  const ppeItems = useFlhaWizardStore((s) => s.ppeItems);
  const ppeInspected = useFlhaWizardStore((s) => s.ppeInspected);
  const hasHighRisk = useFlhaWizardStore((s) => s.hasHighRiskTasks);

  const hazardsPpeStep = hasHighRisk ? 3 : 2;

  switch (step) {
    case 0:
      return !!projectId;
    case 1:
      return selectedTaskIds.length > 0;
    case 2: {
      if (hasHighRisk) return true;
      return validateHazardsControlsPpe(hazards, ppeItems, ppeInspected);
    }
    default: {
      if (step === hazardsPpeStep) {
        return validateHazardsControlsPpe(hazards, ppeItems, ppeInspected);
      }
      return true;
    }
  }
}

function validateHazardsControlsPpe(
  hazards: {
    selected: boolean;
    riskBefore: string | null;
    riskAfter: string | null;
    selectedControlIds: string[];
    customControls: string[];
  }[],
  ppeItems: { confirmed: boolean }[],
  ppeInspected: boolean
): boolean {
  const selectedHazards = hazards.filter((h) => h.selected);
  if (
    selectedHazards.length > 0 &&
    !selectedHazards.every((h) => h.riskBefore !== null)
  ) {
    return false;
  }
  if (
    selectedHazards.length > 0 &&
    !selectedHazards.every(
      (h) =>
        (h.selectedControlIds.length > 0 ||
          (h.customControls &&
            h.customControls.filter((c) => c.trim()).length > 0)) &&
        h.riskAfter !== null
    )
  ) {
    return false;
  }
  if (
    ppeItems.length > 0 &&
    !(ppeItems.every((p) => p.confirmed) && ppeInspected)
  ) {
    return false;
  }
  return true;
}

function FlhaWizardV2() {
  const hydrated = useFlhaWizardHydrated();
  const currentStep = useFlhaWizardStore((s) => s.currentStep);
  const flhaId = useFlhaWizardStore((s) => s.flhaId);
  const projectName = useFlhaWizardStore((s) => s.projectName);
  const setFlhaId = useFlhaWizardStore((s) => s.setFlhaId);
  const setStep = useFlhaWizardStore((s) => s.setStep);
  const selectedTaskIds = useFlhaWizardStore((s) => s.selectedTaskIds);
  const setHasHighRiskTasks = useFlhaWizardStore((s) => s.setHasHighRiskTasks);
  const canNext = useStepValidation(currentStep);
  const navigate = useNavigate();
  const [showDiscard, setShowDiscard] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedFlhaId, setSubmittedFlhaId] = useState<string | null>(null);
  const [submittedProjectName, setSubmittedProjectName] = useState("");
  const { saveDraft: saveLocalDraft } = useOfflineFlha();
  const { data: session } = useSession();

  const steps = useSteps();

  // Fetch task catalog to detect high-risk tasks (same as V1)
  const { data: tasksData } = useQuery({
    queryKey: ["catalogs", "tasks"],
    queryFn: async () => {
      try {
        const data = await apiGet<{ data: TaskCatalogItem[] }>("/api/catalogs/tasks");
        return data;
      } catch {
        // Offline fallback: read from IndexedDB catalog cache
        const cached = await getCatalog<{ data: TaskCatalogItem[] }>("tasks");
        if (cached) return cached;
        throw new Error("No cached tasks data available");
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!tasksData?.data) return;
    const hasHighRisk = tasksData.data.some(
      (t) => selectedTaskIds.includes(t.id) && t.isHighRisk
    );
    setHasHighRiskTasks(hasHighRisk);
  }, [selectedTaskIds, tasksData, setHasHighRiskTasks]);

  // Create FLHA mutation (same as V1)
  const createFlhaMutation = useMutation({
    mutationFn: async () => {
      const state = useFlhaWizardStore.getState();
      const createBody = {
        projectId: state.projectId,
        crewId: state.crewIds[0] || null,
        workArea: state.workArea,
        shift: state.shift,
      };

      if (!isOnline()) {
        const clientId = nanoid();
        const userId = session?.user?.id || "unknown";
        await saveLocalDraft(clientId, null, state, userId);
        await enqueue(
          "POST",
          "/api/flha",
          { ...createBody, id: clientId },
          clientId,
          SYNC_PRIORITY.FLHA_DRAFT
        );
        return { data: { id: clientId, supervisorUserId: null } };
      }

      return apiPost<{
        data: { id: string; supervisorUserId: string | null };
      }>("/api/flha", createBody);
    },
    onSuccess: (res) => {
      setFlhaId(res.data.id);
      if (res.data.supervisorUserId) {
        useFlhaWizardStore
          .getState()
          .updateSetup({ supervisorUserId: res.data.supervisorUserId });
      }
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : "Failed to create FLHA"
      );
    },
  });

  const handleDiscard = () => {
    useFlhaWizardStore.getState().reset();
    localStorage.removeItem("flha-wizard-draft");
    setShowDiscard(false);
    navigate({ to: "/" });
  };

  if (!hydrated) return <WizardSkeleton />;

  if (submitted && submittedFlhaId) {
    return (
      <SubmitSuccess
        flhaId={submittedFlhaId}
        projectName={submittedProjectName || "Project"}
        date={new Date().toLocaleDateString()}
      />
    );
  }

  if (reviewMode && flhaId) {
    return (
      <ReviewScreen
        flhaId={flhaId}
        onEditSection={(stepIndex) => {
          setReviewMode(false);
          setStep(stepIndex);
        }}
        onSubmitSuccess={() => {
          setSubmittedFlhaId(flhaId);
          setSubmittedProjectName(projectName || "Project");
          setSubmitted(true);
        }}
      />
    );
  }

  const StepComponent = steps[currentStep];

  const handleBeforeNext = async (): Promise<boolean> => {
    if (currentStep === 0 && !flhaId) {
      try {
        await createFlhaMutation.mutateAsync();
        return true;
      } catch {
        return false;
      }
    }
    return true;
  };

  return (
    <>
      {/* Discard button in top-right corner — sits on top of the slate header */}
      <div className="max-w-lg mx-auto flex justify-end pt-2 pr-2 absolute top-0 right-0 left-0 z-50 pointer-events-none">
        <Button
          variant="ghost"
          size="sm"
          className="text-white/80 hover:text-white hover:bg-white/10 pointer-events-auto"
          onClick={() => setShowDiscard(true)}
        >
          <X className="h-4 w-4 mr-1" />
          Cancel
        </Button>
      </div>

      <WizardShellV2
        canNext={canNext}
        onBeforeNext={handleBeforeNext}
        onReview={() => setReviewMode(true)}
      >
        <StepComponent />
      </WizardShellV2>

      <AlertDialog open={showDiscard} onOpenChange={setShowDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this draft?</AlertDialogTitle>
            <AlertDialogDescription>
              Your progress will be saved on the server, but the local draft
              will be cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={handleDiscard}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
